import type { ReactNode } from "react";
import { ButtonLink } from "./ButtonLink";
import { BittensorIcon, DiscordIcon, GithubIcon } from "./brand-icons";
import { BookIcon, ExternalIcon } from "./icons";
import { NavMenu } from "./NavMenu";
import { NavDrawer } from "./NavDrawer";
import { SomaMark } from "./SomaMark";
import { ThemeSwitch } from "./ThemeSwitch";
import { siteCopy } from "./site";
import type { Link } from "./types";
import { cn, onSite } from "./utils";

/** The header's height, in px. Docs publish it to Fumadocs as --fd-nav-height. */
export const NAV_HEIGHT_PX = 64;

/**
 * The site header, shared by every app. It carries no in-page anchors - those belong to
 * the page that has them (see the home page's section bar) and would be dead links
 * everywhere else.
 *
 * `links` are extra destinations from the caller; the dashboard and the app come from
 * deployment config, so they are assembled here rather than repeated in every app's content.
 * Leave `docsUrl` out in the docs app itself, where the link would point at the current page.
 *
 * `siteUrl` is for an app that is not the marketing site: every root-relative place
 * (contact, dashboard, somarizer) belongs to the site, so it is resolved against it.
 *
 * `fixed` is for a shell that already reserves the header's height (Fumadocs does, via
 * --fd-nav-height); sticky there would push the content down twice. `fluid` drops the
 * centred column for a full-width bar, which is what a sidebar layout needs.
 */
export function SiteNav({
  links = [],
  appUrl,
  docsUrl,
  dashboardUrl,
  siteUrl = "",
  homeHref = "#top",
  contactHref = "/contact",
  actions,
  fixed = false,
  fluid = false,
  drawer = true,
}: {
  links?: Link[];
  appUrl: string;
  docsUrl?: string;
  dashboardUrl: string;
  /** Base for root-relative hrefs. Empty on the marketing site itself. */
  siteUrl?: string;
  homeHref?: string;
  /** Absolute from the domain root, so it also resolves from the docs' sub-path. */
  contactHref?: string;
  /** Rendered before the buttons: the docs put their search and sidebar triggers here. */
  actions?: ReactNode;
  fixed?: boolean;
  fluid?: boolean;
  /** Off where the shell has its own mobile menu - the docs sidebar drawer, for instance. */
  drawer?: boolean;
}) {
  const t = siteCopy;
  const dashboard = onSite(dashboardUrl, siteUrl);
  // Docs carries an icon because it is the destination people scan for. The dashboard is
  // a button further along, where tau marks it for the miners who come for it.
  const places: (Link & { icon?: ReactNode; external?: boolean })[] = [
    { label: t.labs, href: onSite("/labs", siteUrl) },
    { label: t.contact, href: onSite(contactHref, siteUrl) },
    { label: t.dendrite, href: t.links.dendrite, external: true },
    ...links,
    ...(docsUrl ? [{ label: t.docs, href: docsUrl, icon: <BookIcon /> }] : []),
  ];

  // The menu's hrefs are written with placeholders, because the products it lists are the
  // same everywhere but their addresses come from the deployment.
  const tools = t.tools.items.map((item) => ({
    ...item,
    href: onSite(item.href.replace("{appUrl}", appUrl).replace("{compressor}", t.links.compressor), siteUrl),
  }));
  return (
    <nav
      aria-label={t.navAria}
      className={cn(
        "inset-x-0 top-0 border-b border-line bg-bg/85 backdrop-blur-md backdrop-saturate-150",
        fixed ? "fixed z-40" : "sticky z-30",
      )}
    >
      <div className={cn("flex h-16 items-center justify-between gap-6", fluid ? "px-4 md:px-6" : "wrap")}>
        <a href={homeHref} className="flex items-center gap-2.5 text-base font-medium tracking-[-0.01em]">
          <SomaMark className="h-[18px] w-[19px] text-accent" />
          {t.brand}
        </a>
        {/* Places sit with the actions, not adrift in the middle: the header reads
            left to right as who we are, then where you can go and what you can do. */}
        <div className="flex items-center gap-6">
          {/* The row appears only once it has room: at 768 it pushes the buttons off-screen. */}
          <div className="hidden lg:block">
            <NavMenu label={t.tools.label} items={tools} />
          </div>
          {places.length > 0 && (
            <ul className="hidden gap-6 lg:flex">
              {places.map((l) => (
                <li key={l.href}>
                  <a
                    href={l.href}
                    target={l.external ? "_blank" : undefined}
                    rel={l.external ? "noreferrer" : undefined}
                    className="flex items-center gap-1.5 text-[13.5px] text-fg-muted transition-colors hover:text-fg"
                  >
                    {l.icon}
                    {l.label}
                    {l.external && <ExternalIcon className="text-fg-faint" />}
                  </a>
                </li>
              ))}
            </ul>
          )}
          <div className="flex items-center gap-2">
            {actions}
            {/* The dashboard is red because it is what the subnet's own audience comes for;
                the app stays dark so the two buttons do not compete. */}
            <div className="hidden sm:block">
              <ButtonLink href={dashboard} variant="accent" size="sm">
                {t.dashboard}
                <BittensorIcon className="relative -top-px h-[9px] w-auto shrink-0" />
              </ButtonLink>
            </div>
            {/* The app opens in its own tab: people come back to the site to read, and
                losing the page they were on to a full-screen app is a one-way trip. */}
            <ButtonLink href={appUrl} variant="primary" size="sm" arrow external>
              {t.openApp}
            </ButtonLink>
            <ThemeSwitch />
            {/* Icon-only, and last: these are for people who already know what they want. */}
            <span className="hidden items-center sm:flex">
              <IconLink href={t.links.discord} label={t.community}>
                <DiscordIcon />
              </IconLink>
              <IconLink href={t.links.github} label={t.repository}>
                <GithubIcon />
              </IconLink>
            </span>
            {drawer && (
              <NavDrawer
                links={[
                  ...places.map(({ label, href }) => ({ label, href })),
                  ...tools.map(({ label, href }) => ({ label, href })),
                  { label: t.dashboard, href: dashboard },
                  { label: t.community, href: t.links.discord },
                  { label: t.repository, href: t.links.github },
                ]}
              />
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}

function IconLink({ href, label, children }: { href: string; label: string; children: ReactNode }) {
  return (
    <a
      href={href}
      title={label}
      aria-label={label}
      target="_blank"
      rel="noreferrer"
      className="grid size-8 place-items-center rounded-full text-fg-muted transition-colors duration-200 hover:bg-bg-sunk hover:text-fg"
    >
      {children}
    </a>
  );
}
